// App-wide dialog replacement for window.alert/confirm/prompt (W-M10).
// Module-level singleton: any code (pages, composables, the design runtime)
// calls appAlert/appConfirm/appPrompt and gets a Promise back; the single
// <AppDialogHost/> mounted in App.vue renders the modal following the design
// system (same overlay/card pattern as Screens.vue modals + SynValuePrompt).
//
//   await appAlert('Something happened');
//   if (!(await appConfirm('Delete X?'))) return;
//   const name = await appPrompt('Component name:', 'New component'); // null = cancelled

import { ref } from 'vue';

export interface AppDialogState {
  kind: 'alert' | 'confirm' | 'prompt';
  message: string;
  title?: string;
  /** prompt: initial input value */
  initial?: string;
  placeholder?: string;
  okLabel?: string;
  cancelLabel?: string;
  /** confirm: style the OK button as destructive */
  danger?: boolean;
  resolve: (value: string | boolean | null) => void;
}

// One dialog at a time; if a second opens it queues behind the first.
const active = ref<AppDialogState | null>(null);
const queue: AppDialogState[] = [];

function show(d: AppDialogState): void {
  if (active.value) queue.push(d);
  else active.value = d;
}

/** Resolve the active dialog (called by AppDialogHost) and pop the queue. */
function settle(value: string | boolean | null): void {
  const d = active.value;
  active.value = queue.shift() ?? null;
  d?.resolve(value);
}

export type AppDialogOpts = Partial<Pick<AppDialogState, 'title' | 'placeholder' | 'okLabel' | 'cancelLabel' | 'danger'>>;

export function appAlert(message: string, opts: AppDialogOpts = {}): Promise<void> {
  return new Promise((res) => show({ kind: 'alert', message, ...opts, resolve: () => res() }));
}

export function appConfirm(message: string, opts: AppDialogOpts = {}): Promise<boolean> {
  return new Promise((res) => show({ kind: 'confirm', message, ...opts, resolve: (v) => res(v === true) }));
}

/** Resolves the entered string, or null when cancelled (window.prompt parity). */
export function appPrompt(message: string, initial = '', opts: AppDialogOpts = {}): Promise<string | null> {
  return new Promise((res) => show({ kind: 'prompt', message, initial, ...opts, resolve: (v) => res(typeof v === 'string' ? v : null) }));
}

/** Host wiring (AppDialogHost.vue). */
export function useAppDialog() {
  return { active, settle };
}
